import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Plus, StickyNote} from 'lucide-react'

interface Nota {
  texto: string
  dataHora: string
}

interface NotasObservacoesProps {
  /** ID do lead no banco */
  leadId: string
  /** Nome da coleção: 'leads2' (adulto) ou 'leads_kids' (kids) */
  colecao: 'leads' | 'leads2' | 'leads_kids'
  /** Notas já existentes vindas do documento do lead */
  notasIniciais?: Nota[]
  /** Título numerado da seção, ex: "4. 📝 Notas e Observações" */
  titulo: string
  /** Callback opcional para sincronizar o lead aberto no modal */
  onNotasAtualizadas?: (notas: Nota[]) => void
}

export default function NotasObservacoes({
  leadId,
  colecao,
  notasIniciais = [],
  titulo,
  onNotasAtualizadas,
}: NotasObservacoesProps) {
  const [notas, setNotas] = useState<Nota[]>(
    Array.isArray(notasIniciais) ? notasIniciais : []
  )
  const [texto, setTexto] = useState('')
  const [salvando, setSalvando] = useState(false)

  const adicionarNota = async () => {
    const conteudo = texto.trim()
    if (!conteudo) {
      toast.error('Escreva uma nota antes de adicionar.')
      return
    }
    setSalvando(true)
    try {
      const novaNota: Nota = {
        texto: conteudo,
        dataHora: new Date().toISOString(),
      }
      // Adiciona ao final do array, sem sobrescrever as anteriores
      const novasNotas = [...notas, novaNota]
      await lumi.entities[colecao].update(leadId, {
        notasObservacoes: novasNotas,
        updatedAt: new Date().toISOString(),
      })
      setNotas(novasNotas)
      setTexto('')
      onNotasAtualizadas?.(novasNotas)
      toast.success('Nota adicionada!')
    } catch (err) {
      console.error('[notas] erro ao salvar nota', err)
      toast.error('Erro ao adicionar a nota. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  // Histórico em ordem cronológica decrescente (mais recente primeiro)
  const notasOrdenadas = [...notas].sort(
    (a, b) => new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime()
  )

  return (
    <section>
      <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-2 pb-2 border-b border-[#EDE0D4]">
        {titulo}
      </h3>
      <p className="text-xs text-[#5F5E5A] italic mb-4">
        Registre aqui anotações sobre conversas e contatos com essa pessoa.
      </p>

      {/* Campo de adicionar nota */}
      <div className="mb-5">
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          rows={3}
          placeholder="Ex: Fiz reunião por chamada e ficou de comprar depois da quinzena."
          className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-sm text-[#2C2C2A] resize-y"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        />
        <button
          onClick={adicionarNota}
          disabled={salvando}
          className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-[#2D6A4F] hover:bg-[#245A42] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          {salvando ? 'Salvando...' : 'Adicionar nota'}
        </button>
      </div>

      {/* Histórico de notas */}
      {notasOrdenadas.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[#5F5E5A] italic bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3">
          <StickyNote className="w-4 h-4 text-[#B08968]" />
          Nenhuma observação registrada ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {notasOrdenadas.map((nota, idx) => (
            <div
              key={`${nota.dataHora}-${idx}`}
              className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3"
            >
              <div className="text-xs text-[#B08968] font-medium mb-1.5">
                {new Date(nota.dataHora).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <div className="text-sm text-[#2C2C2A] whitespace-pre-wrap">
                {nota.texto}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
